import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  signal,
  ViewChild,
  OnChanges,
} from '@angular/core'
import { animate, state, style, transition, trigger } from '@angular/animations'
import { MatTab, MatTabChangeEvent, MatTabContent, MatTabGroup } from '@angular/material/tabs'
import { ButtonComponent } from '@shared/button/button.component'
import { environment } from '../../../environments/environment'
import { SelectedUserPreferences } from '../../core/model/reinsurer.model'
import { BasicControl } from '../../management-information/model/management-information.model'
import { TrackingData, TrackingSelectors, TrackingViewsData } from '../tracking.model'
import { TrackingHeaderComponent } from '../tracking-header/tracking-header.component'
import { TrackingSideBarComponent } from '../tracking-sidebar/tracking-sidebar.component'
import { TrackingMainComponent } from '../tracking-main/tracking-main.component'
import { TrackingViewsComponent } from '../tracking-views/tracking-views.component'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-tracking',
  templateUrl: './tracking.component.html',
  styleUrls: ['./tracking.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  imports: [
    TrackingHeaderComponent,
    TrackingMainComponent,
    TrackingViewsComponent,
    TrackingSideBarComponent,
    MatTabGroup,
    MatTab,
    MatTabContent,
    ButtonComponent,
  ],
})
export class TrackingComponent implements OnChanges {
  name = 'Tracking Module'
  showSidebar = signal(true)
  isProduction = environment.production

  @Input() trackingData: TrackingData[] | null = []
  @Input() trackingMyViewsData: TrackingViewsData[] | null = []
  @Input() trackingTeamViewsData: TrackingViewsData[] | null = []
  @Input() trackingClientViewsData: TrackingViewsData[] | null = []
  @Input() selectors: TrackingSelectors | null = null
  @Input() myViewSelectors: TrackingSelectors | null = null
  @Input() teamViewSelectors: TrackingSelectors | null = null
  @Input() isLoading: boolean = false
  @Input() isDetailsLoading: boolean = false
  @Input() inceptionInterval?: BasicControl
  @Input() userPreferences: SelectedUserPreferences[] = []
  @Input() usersResponse: any
  @Output() updateUserValue = new EventEmitter<{
    opportunityId: string | number
    email_id: string[]
    OpportunityTeamRoles: string
    viewType: string
  }>()

  @Output() filtersChanged = new EventEmitter<{
    filters: Record<string, string[]>
  }>()
  @Output() filtersMyViewChanged = new EventEmitter<{
    filters: Record<string, string[]>
  }>()
  @Output() filtersTeamViewChanged = new EventEmitter<{
    filters: Record<string, string[]>
  }>()
  @Output() fetchTrackingDetails = new EventEmitter<number>()
  @Output() inceptionDateChanged = new EventEmitter<{
    minInceptionDate: string
    maxInceptionDate: string
  }>()
  @Output() saveTrackingPreferences = new EventEmitter<{ filters?: Record<string, string[]> }>()
  @Output() setTrackingPreferences = new EventEmitter<{ id: number }>()
  @Output() deleteTrackingPreferences = new EventEmitter<{
    deletedPreferences: SelectedUserPreferences
  }>()

  @Output() updateTrackingPreferences = new EventEmitter<{
    updatePreferences: SelectedUserPreferences
  }>()

  @Output() updateColumnValue = new EventEmitter<{
    opportunityId: string | number
    category: string
    newValue: any
    dateTime?: string
    viewType: string
  }>()

  @Output() updateComment = new EventEmitter<{
    opportunityId: string | number
    category: string
    comment: string
    viewType: string
  }>()
  @Output() updateComplete = new EventEmitter<number>()
  @ViewChild('myViewRef') myViewComponent!: TrackingViewsComponent

  filtersData: Record<string, string[]> = {}
  currentSelectors: TrackingSelectors | null = null
  currenttrackingViewsData: TrackingViewsData[] | null = []
  currentView: string = 'myView'

  ngOnChanges(): void {
    if (this.currentView === 'myView') {
      this.currentSelectors = this.myViewSelectors
      this.currenttrackingViewsData = this.trackingMyViewsData
    } else if (this.currentView === 'teamView') {
      this.currentSelectors = this.teamViewSelectors
      this.currenttrackingViewsData = this.trackingTeamViewsData
    } else {
      this.currentSelectors = this.selectors
    }
  }
  filtersChange(props: { filters: Record<string, string[]>; isSet: boolean }): void {
    this.filtersData = props.filters
    if (props.isSet) {
      if (this.currentView === 'myView') {
        this.filtersMyViewChanged.emit(props)
      } else if (this.currentView === 'teamView') {
        this.filtersTeamViewChanged.emit(props)
      } else {
        this.filtersChanged.emit(props)
      }
    }
  }
  filtersChangeMyView(props: { filters: Record<string, string[]>; isSet: boolean }): void {
    this.filtersData = props.filters
    if (props.isSet) {
      this.filtersMyViewChanged.emit(props)
    }
  }
  onExportClick(): void {
    this.myViewComponent.exportToExcel()
  }

  onTabChange(event: MatTabChangeEvent) {
    if (event.index === 0) {
      this.currentSelectors = this.myViewSelectors
      this.currentView = 'myView'
      this.currenttrackingViewsData = this.trackingMyViewsData
    } else if (event.index === 1) {
      this.currentSelectors = this.teamViewSelectors
      this.currentView = 'teamView'
      this.currenttrackingViewsData = this.trackingTeamViewsData
    } else {
      this.currentSelectors = this.clearSelectedValues(this.selectors)
      this.currentView = 'defaultview'
      this.filtersChange({
        filters: { clientName: [], year: [], businessUnit: [], reinsurerNames: [], complete: [] },
        isSet: true,
      })
    }
  }

  private clearSelectedValues(selectors: TrackingSelectors): TrackingSelectors {
    return {
      clientName: { ...selectors.clientName, selectedValues: [] },
      year: { ...selectors.year, selectedValues: [] },
      businessUnit: { ...selectors.businessUnit, selectedValues: [] },
      complete: { ...selectors.complete, selectedValues: [] },
      reinsurerNames: { ...selectors.reinsurerNames, selectedValues: [] },
    }
  }

  onFilteredSelectorsChange(filteredSelectors: TrackingSelectors): void {
    if (this.currentView === 'defaultview' && this.currentSelectors) {
      this.currentSelectors = {
        clientName: {
          ...filteredSelectors.clientName,
          selectedValues: this.currentSelectors.clientName?.selectedValues || [],
        },
        year: {
          ...filteredSelectors.year,
          selectedValues: this.currentSelectors.year?.selectedValues || [],
        },
        businessUnit: {
          ...filteredSelectors.businessUnit,
          selectedValues: this.currentSelectors.businessUnit?.selectedValues || [],
        },
        complete: {
          ...filteredSelectors.complete,
          selectedValues: this.currentSelectors.complete?.selectedValues || [],
        },
        reinsurerNames: {
          ...filteredSelectors.reinsurerNames,
          selectedValues: this.currentSelectors.reinsurerNames?.selectedValues || [],
        },
      }
    }
  }
}
